import { motion } from "framer-motion";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TEAM_IMG = "https://static.prod-images.emergentagent.com/jobs/4f24f9fb-348c-474e-99ca-a0ad8f55963c/images/d69e1ed3559eadb22063b38946e41d5bcb05cef24f30690427a3ff5a3dfdb259.jpeg";

const PILLARS = [
  { n: "01", title: "Clinical Objective First", body: "Every case begins with understanding the dentist's diagnosis, treatment goals, preferences and clinical priorities — not simply moving teeth to an idealized digital position." },
  { n: "02", title: "Biomechanics-Aware Planning", body: "We evaluate tooth movement, staging, anchorage requirements, attachments, IPR, occlusion and treatment limitations to create a more considered digital treatment strategy." },
  { n: "03", title: "Doctor-Centered, Not Software-Centered", body: "The treating clinician remains in control. OrthoPlaniX provides the planning expertise and digital execution while the dentist makes the final clinical decision." },
  { n: "04", title: "Platform-Independent", body: "We are designed to work across different digital orthodontic workflows and aligner platforms rather than forcing clinicians into one proprietary ecosystem." },
  { n: "05", title: "Plan → Review → Refine", body: "Treatment planning should be collaborative. The dentist reviews the proposed plan, provides feedback and requests modifications before approving the final treatment plan." },
  { n: "06", title: "Consistency at Scale", body: "We give individual dentists, growing clinics, dental laboratories and aligner companies access to a repeatable professional planning workflow — without the cost and complexity of building a large in-house planning team." },
];

export default function Approach() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-20 lg:pb-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>Our Approach</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-4xl">
              <LineMask delay={0.1}>We don't replace</LineMask>
              <LineMask delay={0.25}>the treating dentist.</LineMask>
              <LineMask delay={0.4} className="text-neon">We extend the team.</LineMask>
            </h1>
            <FadeIn delay={0.6}>
              <p className="mt-8 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed">
                Our mission is to make expert clear-aligner treatment planning accessible, consistent and scalable for dental professionals worldwide. We combine clinical thinking, digital orthodontic planning and the dentist's own treatment philosophy.
              </p>
            </FadeIn>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
            <FadeIn>
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <img src={TEAM_IMG} alt="3D clear aligner treatment planning workspace with staging sequence" className="w-full h-[420px] object-cover" />
              </div>
            </FadeIn>
            <div>
              <SectionTag>What Makes Us Different</SectionTag>
              <FadeIn>
                <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                  Not just a digital setup. <span className="text-neon">A considered strategy.</span>
                </h2>
              </FadeIn>
              <FadeIn delay={0.1}>
                <p className="mt-6 text-slate-400 leading-relaxed text-sm md:text-base">
                  Our differentiation is not simply that we can create a digital setup. Every plan follows a deliberate chain of reasoning:
                </p>
                <p className="mt-6 font-display font-bold text-lg md:text-xl text-white leading-relaxed border-l-2 border-neon pl-5">
                  Clinical Objective → Case Assessment → Digital Strategy → Biomechanical Planning → Staging → Doctor Review → Final Plan
                </p>
              </FadeIn>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-ink-2/40">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>Six Principles</SectionTag>
            <div className="mt-12 grid md:grid-cols-2 gap-x-14">
              {PILLARS.map((p, i) => (
                <FadeIn key={p.n} delay={(i % 2) * 0.08}>
                  <div className="border-t border-white/10 py-8 lg:py-10 grid grid-cols-[auto_1fr] gap-6">
                    <span className="font-display font-black text-4xl text-neon/20 leading-none">{p.n}</span>
                    <div>
                      <h3 className="font-display font-bold text-xl lg:text-2xl tracking-tight">{p.title}</h3>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 text-center">
          <div className="max-w-3xl mx-auto px-6">
            <FadeIn>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Ready to extend your team?</h2>
              <Link to="/register" data-testid="approach-cta-btn"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,229,255,0.45)]">
                Start your first case <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
